/**
 * Builds MUI PaletteOptions per colour mode.
 *
 * Native MUI slots consume the cool-slate token values directly.
 * Augmented slots (surface, ink, bars, statusSoft, accent) extend the palette
 * with v2-specific tokens — see augmentation.d.ts for type declarations.
 *
 * Existing MUI semantic mains (success/warning/error/info) are preserved
 * to avoid regressing existing Alert/Chip/Pill components. Only the soft
 * tints are new (palette.statusSoft).
 */

import type { PaletteOptions } from '@mui/material/styles';
import type { BarIndex } from './tokens';
import { ACCENTS, coolSlate, statusSoft, statusInk, bars } from './tokens';

export function buildPalette(isDark: boolean): PaletteOptions {
  const sl = isDark ? coolSlate.dark : coolSlate.light;
  const acc = isDark ? ACCENTS.indigo.dark : ACCENTS.indigo.light;
  const ss = isDark ? statusSoft.dark : statusSoft.light;
  const si = isDark ? statusInk.dark : statusInk.light;
  const b = isDark ? bars.dark : bars.light;

  return {
    mode: isDark ? 'dark' : 'light',

    // ── Accent → primary ───────────────────────────────────────────────────
    primary: {
      // light = opposite-mode accent (usable as a softer tint)
      light: isDark ? ACCENTS.indigo.light.accent : ACCENTS.indigo.dark.accent,
      main:         acc.accent,
      dark:         acc.hover,
      contrastText: acc.onAccent,
    },

    // ── Secondary (unchanged from existing theme) ──────────────────────────
    secondary: {
      light: '#C684FF',
      main:  '#8E33FF',
      dark:  '#5119B7',
      contrastText: '#FFFFFF',
    },

    // ── Native surface tokens ──────────────────────────────────────────────
    background: {
      default: sl.bg,
      paper:   sl.surface,
    },

    // ── Native ink tokens ──────────────────────────────────────────────────
    text: {
      primary:   sl.ink,
      secondary: sl.ink2,
      disabled:  sl.ink3,
    },

    // ── Native line token ──────────────────────────────────────────────────
    divider: sl.line,

    // ── Existing semantic status mains — do NOT change these ──────────────
    // Changing them would regress every existing Alert, Chip, and Pill.
    error: {
      light: '#FFAC82',
      main:  '#FF5630',
      dark:  '#B71D18',
      contrastText: '#FFFFFF',
    },
    warning: {
      light: '#FFD666',
      main:  '#FFAB00',
      dark:  '#B76E00',
      contrastText: '#1C252E',
    },
    info: {
      light: '#61F3F3',
      main:  '#00B8D9',
      dark:  '#006C9C',
      contrastText: '#FFFFFF',
    },
    success: {
      light: '#77ED8B',
      main:  '#22C55E',
      dark:  '#118D57',
      contrastText: '#FFFFFF',
    },

    // ── Augmented slots ────────────────────────────────────────────────────
    surface: {
      sidebar: sl.sidebar,
      raised:  sl.surface2,
      faint:   sl.surface2Faint,
      sunk:    sl.surfaceSunk,
    },

    ink: {
      faint:  sl.ink4,
      line:   sl.line2,
      strong: sl.lineStrong,
    },

    bars: b as Record<BarIndex, { fill: string; ink: string }>,

    statusSoft: {
      ok:     ss.ok,
      warn:   ss.warn,
      danger: ss.danger,
      info:   ss.info,
    },

    statusInk: {
      ok:     si.ok,
      warn:   si.warn,
      danger: si.danger,
      info:   si.info,
    },

    accent: {
      tint: acc.tint,
      soft: acc.soft,
    },
  };
}
